import {
  queryOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'

import {
  acceptSeasonApplicants,
  addSeasonParticipants,
  completeSeasonPeriod,
  createSeason,
  deleteSeason,
  getSeason,
  getSeasonApplications,
  getSeasonApplicationRuleCatalog,
  getSeasonContext,
  getSeasonEligibleCount,
  getSeasonTeams,
  getTeamSeasonMemberships,
  instantiateSeasonPeriod,
  listSeasons,
  preflightSeason,
  publishSeason,
  rejectSeasonApplicants,
  removeSeasonParticipant,
  resolveSeasonTeams,
  updateSeason,
} from '@/modules/season/season.service'
import { SEASON_QUERY_KEYS } from '@/modules/season/season.constants'
import type {
  TSeason,
  TSeasonDraft,
  TSeasonNotice,
} from '@/modules/season/season.types'

type TMutationError = Error

export const seasonListQueryOptions = () =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.list,
    queryFn: listSeasons,
    staleTime: 0,
  })

export const seasonDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.detail(id),
    queryFn: () => getSeason(id),
    staleTime: 0,
  })

export const seasonApplicationsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.applications(id),
    queryFn: () => getSeasonApplications(id),
    staleTime: 0,
  })

export const seasonApplicationRuleCatalogQueryOptions = () =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.applicationRuleCatalog,
    queryFn: getSeasonApplicationRuleCatalog,
    staleTime: 5 * 60 * 1000,
  })

// Per-season team table (participation + status + aggregated standings).
export const seasonTeamsQueryOptions = (
  id: string,
  page: number,
  per_page: number,
  q?: string
) =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.teams(id, page, per_page, q ?? ''),
    queryFn: () => getSeasonTeams(id, page, per_page, q),
    staleTime: 0,
  })

// A team's season memberships + current league (team detail page).
export const teamSeasonMembershipsQueryOptions = (teamId: string) =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.teamMemberships(teamId),
    queryFn: () => getTeamSeasonMemberships(teamId),
    staleTime: 0,
  })

// Backs the builder: teams + tags + game modes + admins.
export const seasonContextQueryOptions = () =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.context,
    queryFn: getSeasonContext,
    staleTime: 0,
  })

// Live eligibility-count preview (debounce the inputs at the call site).
export const seasonEligibleCountQueryOptions = (
  tagIds: string[],
  min: number | null,
  max: number | null
) =>
  queryOptions({
    queryKey: SEASON_QUERY_KEYS.eligibleCount(tagIds, min, max),
    queryFn: () =>
      getSeasonEligibleCount({
        team_tag_ids: tagIds,
        min_team_size: min,
        max_team_size: max,
      }),
    staleTime: 0,
  })

export const useCreateSeason = (
  options?: UseMutationOptions<TSeason, TMutationError, TSeasonDraft>
) => useMutation({ mutationFn: createSeason, ...(options ?? {}) })

export const useUpdateSeason = (
  options?: UseMutationOptions<
    TSeason,
    TMutationError,
    { id: string; draft: TSeasonDraft }
  >
) =>
  useMutation({
    mutationFn: ({ id, draft }) => updateSeason(id, draft),
    ...(options ?? {}),
  })

export const useDeleteSeason = (
  options?: UseMutationOptions<void, TMutationError, string>
) => useMutation({ mutationFn: deleteSeason, ...(options ?? {}) })

export const usePublishSeason = (
  options?: UseMutationOptions<TSeason, TMutationError, string>
) => useMutation({ mutationFn: publishSeason, ...(options ?? {}) })

// Validate a saved season; returns the notice list (variable = season id).
export const usePreflightSeason = (
  options?: UseMutationOptions<TSeasonNotice[], TMutationError, string>
) => useMutation({ mutationFn: preflightSeason, ...(options ?? {}) })

// ---- participant curation (all resolve to the refreshed season) ------------

export const useResolveTeams = (
  options?: UseMutationOptions<TSeason, TMutationError, { id: string }>
) =>
  useMutation({
    mutationFn: ({ id }) => resolveSeasonTeams(id),
    ...(options ?? {}),
  })

export const useAddParticipants = (
  options?: UseMutationOptions<
    TSeason,
    TMutationError,
    { id: string; teamIds: string[] }
  >
) =>
  useMutation({
    mutationFn: ({ id, teamIds }) => addSeasonParticipants(id, teamIds),
    ...(options ?? {}),
  })

export const useAcceptApplicants = (
  options?: UseMutationOptions<
    TSeason,
    TMutationError,
    { id: string; teamIds: string[]; reason?: string }
  >
) =>
  useMutation({
    mutationFn: ({ id, teamIds, reason }) =>
      acceptSeasonApplicants(id, teamIds, reason),
    ...(options ?? {}),
  })

export const useRejectApplicants = (
  options?: UseMutationOptions<
    TSeason,
    TMutationError,
    { id: string; teamIds: string[]; reason?: string }
  >
) =>
  useMutation({
    mutationFn: ({ id, teamIds, reason }) =>
      rejectSeasonApplicants(id, teamIds, reason),
    ...(options ?? {}),
  })

export const useRemoveParticipant = (
  options?: UseMutationOptions<
    TSeason,
    TMutationError,
    { id: string; teamId: string }
  >
) =>
  useMutation({
    mutationFn: ({ id, teamId }) => removeSeasonParticipant(id, teamId),
    ...(options ?? {}),
  })

// Manually start a pending period (dashboard "Başlat"). Variable = period id.
export const useInstantiateSeasonPeriod = (
  options?: UseMutationOptions<TSeason, TMutationError, string>
) =>
  useMutation({
    mutationFn: (periodId: string) => instantiateSeasonPeriod(periodId),
    ...(options ?? {}),
  })

// Manually complete a finished period (dashboard "Bitir"). Variable = period id.
export const useCompleteSeasonPeriod = (
  options?: UseMutationOptions<TSeason, TMutationError, string>
) =>
  useMutation({
    mutationFn: (periodId: string) => completeSeasonPeriod(periodId),
    ...(options ?? {}),
  })
